import { useState } from "react";

import { ChartFilter } from "./ChartFilter";
import { InteractiveFilter } from "./InteractiveFilters/InteractiveFilter";
import { FilterBlockEditor } from "./InteractiveFilters/FilterBlockEditor";
import { FilterDetailsView } from "@/appComponents/Navigation/RightSidebar/Utils/FilterDetailsView";
import { isInReporting } from "@coreapp/utils/isInReporting";
import { useFilterController } from "./useFilterController";
import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";

export const FilterPanel = props => {
  const { doc, canShowInteractiveFilter } = props;
  const filterCtrl = useFilterController();
  const { filterBlockState } = filterCtrl;
  const [editingSharedDoc, setEditingSharedDoc] = useState(null);
  const [detailsView, setDetailsView] = useState(null);
  const [returnTab, setReturnTab] = useState(0);
  const { addToast } = useToast();

  const handleEditSharedFilter = filter => {
    const block = new QFilterBlock();
    block.LoadFromJSON({
      ...filter.definition,
      enabled: true
    });
    setEditingSharedDoc(filter);
    filterCtrl.handleStartEditing(block);
  };

  const handleStopEditingShared = () => {
    setEditingSharedDoc(null);
    filterCtrl.handleStopEditing();
  };

  const handleViewFilterDetails = data => {
    if (data.returnTab != null) {
      setReturnTab(data.returnTab);
    } else {
      // Fallback for callers that don't inject returnTab directly.
      const customChartOffset = doc.IsCustomChart() ? 1 : 0;
      setReturnTab((data.sourceTab === "shared" ? 1 : 0) + customChartOffset);
    }
    setDetailsView(data);
  };

  const onEditFilterDetailsClosure = tabIndex => () => {
    setDetailsView(null);
    setReturnTab(tabIndex);
  };

  if (detailsView) {
    return (
      <FilterDetailsView
        entityType="filter"
        name={detailsView.name}
        ownerDisplayName={detailsView.ownerDisplayName}
        isShared={detailsView.isShared}
        userRole={detailsView.userRole}
        rules={detailsView.rules}
        onBack={() => setDetailsView(null)}
        shareProps={
          detailsView.shareProps && !detailsView.shareProps.sharedDoc
            ? {
                ...detailsView.shareProps,
                onShareComplete: () => {
                  setDetailsView(null);
                  detailsView.shareProps.onShareComplete?.();
                }
              }
            : detailsView.shareProps
        }
        onEdit={
          detailsView.onEdit
            ? () => {
                setDetailsView(null);
                detailsView.onEdit();
              }
            : undefined
        }
        onCopy={
          detailsView.onCopy
            ? () => {
                detailsView.onCopy();
                setDetailsView(null);
                addToast({
                  ...(detailsView.sourceTab === "shared"
                    ? createBoldToastMessage("Shared filter copied", {
                        entity: detailsView.name
                      })
                    : createBoldToastMessage("Entity copy created", { entity: detailsView.name })),
                  status: TOAST_STATUS.SUCCESS
                });
              }
            : undefined
        }
        onDelete={
          detailsView.onDelete
            ? () => {
                setDetailsView(null);
                detailsView.onDelete();
                addToast({
                  ...createBoldToastMessage("Entity deleted", { entity: detailsView.name }),
                  status: TOAST_STATUS.SUCCESS
                });
              }
            : undefined
        }
      />
    );
  }

  if (isInReporting() && !filterBlockState.filterBlock) {
    return <InteractiveFilter filterCtrl={filterCtrl} />;
  }

  if (filterBlockState.filterBlock) {
    return (
      <FilterBlockEditor
        filterCtrl={filterCtrl}
        sharedFilterDoc={editingSharedDoc}
        onStopEditingShared={handleStopEditingShared}
      />
    );
  }

  return (
    <ChartFilter
      doc={doc}
      canShowInteractiveFilter={canShowInteractiveFilter}
      filterCtrl={filterCtrl}
      onEditSharedFilter={handleEditSharedFilter}
      onViewFilterDetails={handleViewFilterDetails}
      onOpenEditClosure={onEditFilterDetailsClosure}
      initialTab={returnTab}
    />
  );
};
